/**
 * A faixa da organização em criação, no detalhe da gestão (#769).
 *
 * Com o pedido da criação guardado, oferece retomar de onde parou; depois, o
 * formulário quando o administrador guardado não serve, o que não terminou, ou
 * o motivo da falha.
 * Sem o pedido (ele fica no máximo 30 dias), o caminho é o formulário de "Nova
 * organização", com o mesmo identificador.
 */

import type { ReactNode } from 'react';
import { diaMesAno } from '@/shared/lib/formato';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { EstadoDaRetomada } from '../lib/retomada';

export function FaixaDaCriacao({
  parouEm,
  pedidoGuardadoAte,
  retomada,
  aoRetomar,
  aoAbrirNovaOrganizacao,
}: {
  /** A parte onde a criação parou ("Atendimento"); nula quando não se sabe. */
  parouEm: string | null;
  /** Até quando o pedido fica guardado, em ISO; nulo quando não há pedido. */
  pedidoGuardadoAte: string | null;
  retomada: EstadoDaRetomada | null;
  /** Sem ele, a faixa só avisa. */
  aoRetomar?: () => void;
  /** Sem ele, a faixa sem pedido, ou com o administrador que não serve, só avisa. */
  aoAbrirNovaOrganizacao?: () => void;
}): ReactNode {
  const onde = parouEm ? `A criação parou no ${parouEm}.` : 'A criação não terminou.';

  if (retomada?.fase === 'retomando') {
    return (
      <Faixa tom="atencao">Retomando a criação. Pode levar um minuto: não feche esta tela.</Faixa>
    );
  }
  if (retomada?.fase === 'refazer') {
    // O Retomar não confirma a conta de outra organização (decisão de 28/10/2026).
    return (
      <Faixa
        tom="atencao"
        {...(aoAbrirNovaOrganizacao
          ? { acao: { rotulo: 'Refazer pelo formulário', aoClicar: aoAbrirNovaOrganizacao } }
          : {})}
      >
        {retomada.texto}
      </Faixa>
    );
  }
  if (pedidoGuardadoAte === null) {
    return (
      <Faixa
        tom="atencao"
        {...(aoAbrirNovaOrganizacao
          ? { acao: { rotulo: 'Abrir Nova organização', aoClicar: aoAbrirNovaOrganizacao } }
          : {})}
      >
        {onde} O pedido dela não está mais guardado, porque fica no máximo 30 dias: preencha de novo
        em Nova organização, com o mesmo identificador.
      </Faixa>
    );
  }
  // Sem quem retome, a faixa não promete o que a tela não faz.
  if (!aoRetomar) return <Faixa tom="atencao">{onde}</Faixa>;
  return (
    <>
      <Faixa tom="atencao" acao={{ rotulo: 'Retomar', aoClicar: () => aoRetomar() }}>
        {onde} Retomar continua de onde parou, sem refazer o que já foi feito, com o pedido guardado
        até {diaMesAno(pedidoGuardadoAte)}.
      </Faixa>
      {retomada?.fase === 'nao_terminou' && retomada.pendencias.length > 0 ? (
        <ul className="organizacao__pendencias" aria-label="Por que não terminou">
          {retomada.pendencias.map((pendencia) => (
            <li key={pendencia}>{pendencia}</li>
          ))}
        </ul>
      ) : null}
      {retomada?.fase === 'falhou' ? (
        <p className="organizacao__falha" role="alert">
          {retomada.motivo}
        </p>
      ) : null}
    </>
  );
}
